/**
 * Upload Manifest Component
 * Handles CSV file upload, parsing, validation, and database insertion
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Save,
} from '@mui/icons-material';
import { useAppStore } from '../../store/appStore';
import { predictionsApi } from '../../services/predictions.service';
import Papa from 'papaparse';
import { Product, ExperimentVersion, TagPredictionType } from '../../types';

interface CSVRow {
  [key: string]: string | null;
}

interface ProcessedPrediction {
  [key: string]: any;
  errors?: string[];
}

interface UploadResult {
  success: boolean;
  id: string;
  message: string;
}

const PRODUCT_OPTIONS = Object.values(Product);
const EXPERIMENT_OPTIONS = Object.values(ExperimentVersion);
const PREDICTION_TYPE_OPTIONS = Object.values(TagPredictionType);

export default function UploadManifest() {
  const { selectedProduct: storeProduct, selectedExperiment: storeExperiment } = useAppStore();
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [processedData, setProcessedData] = useState<ProcessedPrediction[]>([]);
  const [processingErrors, setProcessingErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product>(storeProduct || Product.IM_MATH);
  const [selectedExperiment, setSelectedExperiment] = useState<ExperimentVersion>(storeExperiment || ExperimentVersion.V0_0_0);
  const [selectedPredictionType, setSelectedPredictionType] = useState<TagPredictionType>(TagPredictionType.LessonV1_SpineTagsPrediction);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile && uploadedFile.type === 'text/csv') {
      setFile(uploadedFile);
      // Reset previous data
      setCsvData([]);
      setProcessedData([]);
      setProcessingErrors([]);
      setUploadResults([]);

      // Parse CSV
      Papa.parse(uploadedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setCsvData(results.data as CSVRow[]);
        },
        error: (error) => {
          setProcessingErrors([`CSV parsing error: ${error.message}`]);
        }
      });
    }
  };

  const processCSVData = () => {
    setIsProcessing(true);
    const processed: ProcessedPrediction[] = [];
    const errors: string[] = [];

    csvData.forEach((row, index) => {
      try {
        const processedRow: ProcessedPrediction = { ...row };
        const rowErrors: string[] = [];

        // Convert numeric fields to strings
        const stringFields = ['unit_number', 'lesson_number'];
        stringFields.forEach(field => {
          if (row[field] != null) {
            processedRow[field] = String(row[field]);
          }
        });

        // Convert lesson_grade to number
        if (row['lesson_grade'] != null) {
          processedRow['lesson_grade'] = Number(row['lesson_grade']);
        }

        // Parse target_cc_standard_codes from comma-separated string
        if (row['target_cc_standard_codes']) {
          const codes = String(row['target_cc_standard_codes'])
            .split(',')
            .map(code => code.trim())
            .filter(code => code.length > 0);
          processedRow['target_cc_standard_codes'] = codes.length > 0 ? codes : null;
        }

        // Parse target_standard_ids from comma-separated string
        if (row['target_standard_ids']) {
          const ids = String(row['target_standard_ids'])
            .split(',')
            .map(id => id.trim())
            .filter(id => id.length > 0);
          processedRow['target_standard_ids'] = ids.length > 0 ? ids : null;
        }

        // Add context from selectors
        processedRow.product = selectedProduct;
        processedRow.experiment_version = selectedExperiment;
        processedRow.tag_prediction_type = selectedPredictionType;

        // Add timestamps
        const now = new Date().toISOString();
        processedRow.created_at = processedRow.created_at || now;
        processedRow.updated_at = processedRow.updated_at || now;

        // Add default review status
        processedRow.review_status = processedRow.review_status || 'PENDING';

        // Validate required fields
        // Note: Use == null to check for null/undefined, not falsy (lesson_grade can be 0)
        const requiredFields = ['lesson_name', 'lesson_subject', 'lesson_grade'];
        requiredFields.forEach(field => {
          if (processedRow[field] == null || processedRow[field] === '') {
            rowErrors.push(`Missing required field: ${field}`);
          }
        });

        if (rowErrors.length > 0) {
          processedRow.errors = rowErrors;
          errors.push(`Row ${index + 1}: ${rowErrors.join(', ')}`);
        }

        processed.push(processedRow);
      } catch (error: any) {
        const errorMsg = `Row ${index + 1}: ${error?.message || 'Unknown error'}`;
        errors.push(errorMsg);
        processed.push({ ...row, errors: [error?.message || 'Unknown error'] });
      }
    });

    setProcessedData(processed);
    setProcessingErrors(errors);
    setIsProcessing(false);
  };

  const uploadToDatabase = async () => {
    setIsUploading(true);
    const results: UploadResult[] = [];

    // Only upload records without errors
    const validRecords = processedData.filter(record => !record.errors?.length);

    for (let i = 0; i < validRecords.length; i++) {
      const record = validRecords[i];
      try {
        await predictionsApi.create(record as any);
        results.push({
          success: true,
          id: record.id || `row-${i + 1}`,
          message: 'Successfully uploaded'
        });
      } catch (error: any) {
        results.push({
          success: false,
          id: record.id || `row-${i + 1}`,
          message: error?.message || 'Upload failed'
        });
      }
    }

    setUploadResults(results);
    setIsUploading(false);
  };

  const validRecords = processedData.filter(record => !record.errors?.length);
  const invalidRecords = processedData.filter(record => record.errors?.length);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Upload Manifest
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Upload a CSV file containing lesson manifest data to create new prediction records.
      </Typography>

      <Grid container spacing={3}>
        {/* Configuration Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={selectedProduct}
                    label="Product"
                    onChange={(e) => setSelectedProduct(e.target.value as Product)}
                  >
                    {PRODUCT_OPTIONS.map((product) => (
                      <MenuItem key={product} value={product}>
                        {product}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Experiment</InputLabel>
                  <Select
                    value={selectedExperiment}
                    label="Experiment"
                    onChange={(e) => setSelectedExperiment(e.target.value as ExperimentVersion)}
                  >
                    {EXPERIMENT_OPTIONS.map((experiment) => (
                      <MenuItem key={experiment} value={experiment}>
                        {experiment}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Prediction Type</InputLabel>
                  <Select
                    value={selectedPredictionType}
                    label="Prediction Type"
                    onChange={(e) => setSelectedPredictionType(e.target.value as TagPredictionType)}
                  >
                    {PREDICTION_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* File Upload Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              File Upload
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              sx={{ mr: 2 }}
            >
              Select CSV File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            {file && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success" icon={<CheckCircle />}>
                  File uploaded: <strong>{file.name}</strong> ({file.size.toLocaleString()} bytes)
                </Alert>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Total rows: {csvData.length}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* CSV Preview Section */}
        {csvData.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  CSV Preview (first 5 rows)
                </Typography>
                <Button
                  variant="contained"
                  onClick={processCSVData}
                  disabled={isProcessing}
                >
                  Process Data
                </Button>
              </Box>
              {isProcessing && <LinearProgress sx={{ mb: 2 }} />}

              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {Object.keys(csvData[0] || {}).map((column) => (
                        <TableCell key={column}>{column}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvData.slice(0, 5).map((row, index) => (
                      <TableRow key={index}>
                        {Object.values(row).map((value, cellIndex) => (
                          <TableCell key={cellIndex}>
                            {value || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Processing Results */}
        {processedData.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Processing Results
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {validRecords.length}
                      </Typography>
                      <Typography variant="body2">
                        Valid Records
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {invalidRecords.length}
                      </Typography>
                      <Typography variant="body2">
                        Invalid Records
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">
                        {processedData.length}
                      </Typography>
                      <Typography variant="body2">
                        Total Rows
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Show errors */}
              {processingErrors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="error">
                    <Typography variant="subtitle2">Processing Errors:</Typography>
                    {processingErrors.slice(0, 10).map((error, index) => (
                      <Typography variant="body2" key={index}>
                        • {error}
                      </Typography>
                    ))}
                    {processingErrors.length > 10 && (
                      <Typography variant="body2">
                        ... and {processingErrors.length - 10} more errors
                      </Typography>
                    )}
                  </Alert>
                </Box>
              )}

              {/* Upload to Database */}
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Save />}
                  onClick={uploadToDatabase}
                  disabled={validRecords.length === 0 || isUploading}
                  sx={{ minWidth: 200 }}
                >
                  {isUploading ? 'Uploading...' : `Upload ${validRecords.length} Records`}
                </Button>
                {invalidRecords.length > 0 && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {invalidRecords.length} invalid records will be skipped
                  </Typography>
                )}
              </Box>

              {isUploading && <LinearProgress sx={{ mt: 2 }} />}
            </Paper>
          </Grid>
        )}

        {/* Upload Results */}
        {uploadResults.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Upload Results
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {uploadResults.filter(r => r.success).length}
                      </Typography>
                      <Typography variant="body2">
                        Successful
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {uploadResults.filter(r => !r.success).length}
                      </Typography>
                      <Typography variant="body2">
                        Failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Record ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadResults.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={result.success ? 'Success' : 'Failed'}
                            color={result.success ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{result.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}